import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api } from '../lib/api';
import { money } from '../lib/format';

interface ZoneRow {
  zoneId: string;
  code: string;
  name: string;
  /** 这个商家在这个大区**单独配**的价;null = 没单独配 */
  price: number | null;
  /** 没单独配时实际生效的那个价 */
  effectivePrice: number;
  /** 那个价从哪来的 */
  effectiveFrom: string;
}

interface Table {
  customer: { id: string; name: string };
  fallbackPrice: number;
  zones: ZoneRow[];
}

interface Customer {
  id: string;
  code: string;
  name: string;
}

/**
 * 商家 × 大区 的基础价。
 *
 * ★ 这件事本来就配得出来(计价规则上有商家和区域两个字段),
 *   但那意味着运营要一条一条建规则,每条里还有十来个字段
 *   (重量阶梯、抽成、税率)要填 —— 实际结果是没人配,
 *   所有商家共用一个兜底价,而真实业务里**每家谈的价都不一样**。
 *
 * ★ 这一屏只管基础价,其余字段跟着这个商家的通用规则走。
 *   一个大区一套重量阶梯的话,改一次要改五遍。
 *
 * ★ 没单独配的格子要显示「实际落的是哪个价、从哪来」——
 *   只留一个空框的话,人会以为「没配 = 不收钱」。
 */
export function CustomerZonePrices({
  canManage,
  /**
   * 锁定到某一家。
   *
   * ★ 商家那一屏是**按商家在想事情**的地方:改完这家的取货拍照,
   *   顺手就该能改这家的价。而在「计价」页里,人得先在下拉框里
   *   把刚才那家再找一遍 —— 中间隔着一次「他叫什么来着」。
   *   同一套逻辑两个入口,不是复制一份。
   */
  fixedCustomerId,
  compact,
}: {
  canManage: boolean;
  fixedCustomerId?: string;
  /** 嵌在别的界面里时,不重复显示标题和说明 */
  compact?: boolean;
}) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState(fixedCustomerId ?? '');
  const [table, setTable] = useState<Table | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    // 锁定了就不用拉商家列表 —— 那个下拉框根本不会显示
    if (fixedCustomerId) return;
    void api<Customer[] | { data: Customer[] }>('/customers')
      .then((r) => {
        const rows = Array.isArray(r) ? r : r.data;
        setCustomers(rows);
        setCustomerId((cur) => cur || (rows[0]?.id ?? ''));
      })
      .catch((e) => setErr(e instanceof ApiFailure ? e.message : '读取商家失败'));
  }, [fixedCustomerId]);

  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const t = await api<Table>(`/pricing/rules/customer-zones/${customerId}`);
      setTable(t);
      setDraft(
        Object.fromEntries(
          t.zones.map((z) => [z.zoneId, z.price == null ? '' : (z.price / 100).toString()]),
        ),
      );
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '读取失败');
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    if (!table) return;
    setBusy(true);
    setSaved(false);
    setErr(null);
    try {
      await api(`/pricing/rules/customer-zones/${table.customer.id}`, {
        method: 'PUT',
        body: {
          prices: table.zones.map((z) => {
            const raw = (draft[z.zoneId] ?? '').trim();
            // 空 = 这个区不单独配,落回上一级
            return { zoneId: z.zoneId, price: raw === '' ? null : Math.round(Number(raw) * 100) };
          }),
        },
      });
      setSaved(true);
      await load();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-label="商家分区价">
      {!compact && (
        <>
          <h2 className="mb-1 text-xl font-semibold text-slate-900 dark:text-slate-100">
            商家 × 大区 基础价
          </h2>
          <p className="mb-4 text-sm text-slate-500">
            每家谈的价不一样,而同一家送到不同大区也不一样。留空表示这个区不单独配 ——
            它会落回这个商家的通用价,再落回兜底价
          </p>
        </>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        {fixedCustomerId && (
          <span className="text-sm text-slate-500">
            留空的大区落回通用价,再落回兜底价
          </span>
        )}
        {!fixedCustomerId && (
        <label className="text-sm text-slate-600 dark:text-slate-300" htmlFor="czp-customer">
          商家
        </label>
        )}
        {!fixedCustomerId && (
        <select
          id="czp-customer"
          aria-label="选择商家"
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm
                     dark:border-slate-600 dark:bg-slate-800"
        >
          {customers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        )}
        {table && (
          <span className="text-sm text-slate-400">
            没单独配的落到 {money(String(table.fallbackPrice))}
          </span>
        )}
      </div>

      {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

      {table && (
        <div className="max-w-2xl overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500 dark:bg-slate-800">
              <tr>
                <th className="px-3 py-2">大区</th>
                <th className="px-3 py-2">基础价(元)</th>
                <th className="px-3 py-2">实际生效</th>
              </tr>
            </thead>
            <tbody>
              {table.zones.map((z) => (
                <tr key={z.zoneId} className="border-t border-slate-100 dark:border-slate-700">
                  <td className="px-3 py-2 text-slate-900 dark:text-slate-100">{z.name}</td>
                  <td className="px-3 py-2">
                    <input
                      aria-label={`${z.name} 基础价`}
                      value={draft[z.zoneId] ?? ''}
                      disabled={!canManage}
                      inputMode="decimal"
                      placeholder="不单独配"
                      onChange={(e) => setDraft({ ...draft, [z.zoneId]: e.target.value })}
                      className="w-28 rounded-lg border border-slate-300 bg-white px-2 py-1
                                 text-right font-mono dark:border-slate-600 dark:bg-slate-800"
                    />
                  </td>
                  {/*
                    ★ 「实际生效多少、从哪来」必须写出来。
                      空着的格子不等于不收钱 —— 它落的是上一级的价,
                      而不写出来的话,人配到一半会以为这个区是免费的。
                  */}
                  <td className="px-3 py-2 text-slate-500">
                    {money(String(z.effectivePrice))}
                    <span className="ml-1 text-xs text-slate-400">{z.effectiveFrom}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canManage && table && (
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={() => void save()}
            disabled={busy}
            className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white
                       disabled:opacity-40"
          >
            {busy ? '保存中…' : '保存'}
          </button>
          {saved && <span className="text-sm text-green-600 dark:text-green-400">已保存</span>}
          {/*
            ★ 说清楚改价只影响以后的单 —— 不说的话,人会担心
              「改完之后上个月的账单是不是也变了」,于是不敢改。
          */}
          <span className="text-xs text-slate-400">
            改价只影响之后建的单;已经建好的单存着当时的明细
          </span>
        </div>
      )}
    </section>
  );
}
